import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faImage, faUser, faTrophy } from '@fortawesome/free-solid-svg-icons';
import type { AchievementPeekItem } from '../../types/achievement';

interface Props {
  item: AchievementPeekItem;
  onClose: () => void;
}

export default function AchievementUnlockedModal({ item, onClose }: Props) {
  const { t, i18n } = useTranslation('achievement');
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const lang = i18n.language as 'ko' | 'en' | 'ja';
  const resolveLocale = (ls: Partial<Record<'ko' | 'en' | 'ja', string>>, fallback: string) =>
    ls[lang] ?? ls.ko ?? fallback;

  const title = resolveLocale(item.title, item.achievementId);
  const description = resolveLocale(item.description, '');
  const titleId = `achievement-modal-title-${item.achievementId}`;

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus close button on mount
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  // ESC key closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <>
      <style>{`
        @keyframes achievement-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes achievement-card-in {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        .achievement-modal-backdrop {
          animation: achievement-backdrop-in 0.25s ease-out;
        }
        .achievement-modal-card {
          animation: achievement-card-in 0.3s ease-out;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="achievement-modal-backdrop"
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        {/* Modal card */}
        <div
          className="achievement-modal-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: '28rem',
            background: 'var(--surface-1, #1a1d24)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '1rem',
            boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.625rem',
              padding: '0.875rem 1rem',
              background: 'rgba(99, 102, 241, 0.12)',
              borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
            }}
          >
            <FontAwesomeIcon
              icon={faTrophy}
              style={{ color: '#a5b4fc', fontSize: '1rem', flexShrink: 0 }}
            />
            <span
              style={{
                flex: 1,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#a5b4fc',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t('unlock_modal.eyebrow')}
            </span>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              aria-label={t('unlock_modal.dismiss')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.45)',
                padding: '0.25rem 0.375rem',
                lineHeight: 1,
                flexShrink: 0,
                borderRadius: '0.25rem',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.85)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; }}
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: '1rem' }} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '1.25rem 1.25rem 0' }}>
            {/* Achievement title */}
            <p
              id={titleId}
              style={{
                margin: '0 0 0.5rem',
                fontWeight: 800,
                fontSize: '1.125rem',
                color: '#f1f5f9',
                lineHeight: 1.25,
              }}
            >
              {title}
            </p>

            {/* Description */}
            {description && (
              <p
                style={{
                  margin: '0 0 1rem',
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.55)',
                  lineHeight: 1.55,
                }}
              >
                {description}
              </p>
            )}

            {/* Asset showcase */}
            {item.assets.length > 0 && (
              <div
                style={{
                  marginBottom: '1.25rem',
                  display: item.assets.length === 1 ? 'flex' : 'grid',
                  ...(item.assets.length === 1
                    ? { justifyContent: 'center' }
                    : {
                        gridTemplateColumns: 'repeat(auto-fill, minmax(7rem, 1fr))',
                        gap: '0.75rem',
                      }),
                }}
              >
                {item.assets.map((asset) => {
                  const label =
                    asset.kind === 'BACKGROUND'
                      ? t('unlock_modal.asset_placeholder_background')
                      : t('unlock_modal.asset_placeholder_avatar');
                  const isSingle = item.assets.length === 1;
                  return (
                    <div
                      key={asset.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.375rem',
                      }}
                    >
                      <div
                        style={{
                          width: isSingle ? '9rem' : '100%',
                          aspectRatio: asset.kind === 'BACKGROUND' ? '16/9' : '1/1',
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(99,102,241,0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {asset.imageUrl ? (
                          <img
                            src={asset.imageUrl}
                            alt={label}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={asset.kind === 'BACKGROUND' ? faImage : faUser}
                            style={{ color: 'rgba(255,255,255,0.3)', fontSize: '2rem' }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: '0.6875rem',
                          color: 'rgba(255,255,255,0.5)',
                          textAlign: 'center',
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: '0.625rem',
              padding: '1rem 1.25rem 1.25rem',
              borderTop: item.assets.length > 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Link
              to="/7/dashboard"
              onClick={onClose}
              style={{
                flex: 1,
                display: 'block',
                textAlign: 'center',
                padding: '0.625rem 0.75rem',
                background: 'rgba(99,102,241,0.2)',
                border: '1px solid rgba(99,102,241,0.4)',
                borderRadius: '0.5rem',
                color: '#a5b4fc',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'background 0.15s',
              }}
            >
              {t('unlock_modal.cta')}
            </Link>
            <button
              onClick={onClose}
              style={{
                padding: '0.625rem 1rem',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '0.5rem',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = 'rgba(255,255,255,0.1)';
                el.style.color = 'rgba(255,255,255,0.85)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = 'rgba(255,255,255,0.06)';
                el.style.color = 'rgba(255,255,255,0.6)';
              }}
            >
              {t('unlock_modal.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
